import type { TicketProgress } from '@shared/schemas';

/**
 * Pure helpers reading the Q-3..Q-6 TicketProgress schema extensions
 * (committed @ 8c208a5 per CHAT_2B_PENDING_QUESTIONS · all Genie=adopt 16 MAY).
 *
 * Designed для future Board.tsx + Ticket.tsx consumers · isolated from
 * foreign-entangled working tree changes. Tests cover all branches +
 * undefined-field fallbacks (schema fields all optional · legacy data
 * loads without them).
 */

const MS_PER_MIN = 60_000;
const MS_PER_HOUR = 60 * MS_PER_MIN;
const MS_PER_DAY = 24 * MS_PER_HOUR;

/* ─── Q-3 · blockedReason ────────────────────────────────────────────────── */

/** True если ticket marked blocked (blockedReason non-empty string). */
export function ticketIsBlocked(ticket: Pick<TicketProgress, 'blockedReason'> | undefined): boolean {
  return Boolean(ticket?.blockedReason && ticket.blockedReason.trim().length > 0);
}

/** Returns trimmed blockedReason text · null если not blocked. */
export function ticketBlockedReason(
  ticket: Pick<TicketProgress, 'blockedReason'> | undefined,
): string | null {
  const r = ticket?.blockedReason?.trim();
  return r && r.length > 0 ? r : null;
}

/* ─── Q-4 · columnChangedAt · age-in-column ──────────────────────────────── */

/**
 * Days since ticket entered current column (status). Uses `columnChangedAt`
 * if available · falls back к `startedAt` if not · returns null если neither
 * timestamp present.
 *
 * Per CHAT_2B Q-4 design: stale-detection accurate для current column.
 * Reference: 0 (today) · 1 (yesterday) · etc. Negative values clamped к 0
 * (defensive · clock skew safety).
 */
export function ticketAgeInColumnDays(
  ticket: Pick<TicketProgress, 'columnChangedAt' | 'startedAt'> | undefined,
  now: Date = new Date(),
): number | null {
  const iso = ticket?.columnChangedAt ?? ticket?.startedAt;
  if (!iso) return null;
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return null;
  const diffMs = now.getTime() - t;
  return Math.max(0, Math.floor(diffMs / MS_PER_DAY));
}

/* ─── Q-5 · assignees · multi-assignee normalization ─────────────────────── */

/**
 * Normalize assignees к array shape. Returns empty array если field absent.
 * Defensive: filters undefined/empty strings.
 */
export function ticketAssignees(
  ticket: Pick<TicketProgress, 'assignees'> | undefined,
): string[] {
  const raw = ticket?.assignees ?? [];
  return raw.filter((id): id is string => typeof id === 'string' && id.length > 0);
}

/** Primary assignee per ADR-004 = assignees[0] для notification routing. */
export function primaryAssignee(
  ticket: Pick<TicketProgress, 'assignees'> | undefined,
): string | null {
  const list = ticketAssignees(ticket);
  return list[0] ?? null;
}

/** True если userId is one of the ticket's assignees. */
export function isAssignedTo(
  ticket: Pick<TicketProgress, 'assignees'> | undefined,
  userId: string,
): boolean {
  return ticketAssignees(ticket).includes(userId);
}

/**
 * Filter helper для Board.tsx assignee dropdown. Returns ticket ids
 * assigned к given userId. `null` userId = unassigned filter (no assignees).
 */
export function filterTicketsByAssignee(
  tickets: Record<string, Pick<TicketProgress, 'assignees'>>,
  userId: string | null,
): string[] {
  const out: string[] = [];
  for (const [id, t] of Object.entries(tickets)) {
    const list = ticketAssignees(t);
    if (userId === null) {
      if (list.length === 0) out.push(id);
    } else if (list.includes(userId)) {
      out.push(id);
    }
  }
  return out;
}

/* ─── Q-6 · updatedAt · relative-time meta ───────────────────────────────── */

/**
 * Format `updatedAt` ISO timestamp как «обновлён N мин назад» style relative
 * Russian label. Returns null если field absent OR invalid. Consumers: Ticket.tsx
 * + Board.tsx card meta line.
 *
 * Thresholds: <1 min «только что» · <60 min «N мин назад» · <24h «N ч назад» ·
 * <7d «N дн назад» · ≥7d ISO date.
 */
export function ticketUpdatedRelative(
  ticket: Pick<TicketProgress, 'updatedAt'> | undefined,
  now: Date = new Date(),
): string | null {
  const iso = ticket?.updatedAt;
  if (!iso) return null;
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return null;
  const diffMs = now.getTime() - t;
  if (diffMs < 0) return 'только что'; // clock skew · defensive
  if (diffMs < MS_PER_MIN) return 'только что';
  if (diffMs < MS_PER_HOUR) {
    const mins = Math.floor(diffMs / MS_PER_MIN);
    return `${mins} мин назад`;
  }
  if (diffMs < MS_PER_DAY) {
    const hours = Math.floor(diffMs / MS_PER_HOUR);
    return `${hours} ч назад`;
  }
  if (diffMs < 7 * MS_PER_DAY) {
    const days = Math.floor(diffMs / MS_PER_DAY);
    return `${days} дн назад`;
  }
  // ≥7d · fall back к ISO date (YYYY-MM-DD)
  return new Date(iso).toISOString().slice(0, 10);
}
